import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Button, Divider, Group, LoadingOverlay, Paper, Stack, Text, Title } from '@mantine/core';
import type { CaminoView } from '@/types/camino';
import styles from './Camino.module.sass';
import CaminoMemberImportForm from './CaminoMemberImportForm';
import CaminoMemberImportsTable from './CaminoMemberImportsTable';
import { EditCaminoModal, Icon } from '@/components';
import { useTabStatusContext } from '@/hooks';
import { useCamino, useUpdateCaminoMutation } from '@/hooks/organization-programs';

const LAYOUT = {
  ARROW_LEFT_ICON_SIZE: 16,
  LOADING_PAPER_MIN_HEIGHT: 200,
};

const Camino = () => {
  const { id: churchId } = useParams<{ id: string }>();
  const { refreshTabStatus } = useTabStatusContext();
  const { data: caminoData, isLoading, isError } = useCamino(churchId);
  const updateCaminoMutation = useUpdateCaminoMutation(churchId ?? '');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  if (!churchId) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Church ID not found</Text>
      </Paper>
    );
  }

  const handleEditCamino = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditCamino = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveCamino = async (updatedData: CaminoView) => {
    await updateCaminoMutation.mutateAsync({
      isActive: updatedData.isActive,
      parishId: updatedData.parishId,
      dioceseId: updatedData.dioceseId,
      url: updatedData.url,
      comment: updatedData.comment,
    });
    setIsEditModalOpen(false);
    refreshTabStatus();
  };

  if (isLoading) {
    return (
      <Paper p="xl" radius="md" w="100%" pos="relative" style={{ minHeight: LAYOUT.LOADING_PAPER_MIN_HEIGHT }}>
        <LoadingOverlay visible />
      </Paper>
    );
  }

  if (isError || !caminoData) {
    return (
      <Paper p="xl" radius="md" w="100%">
        <Text c="red">Failed to load camino data</Text>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" radius="md" w="100%">
        <Stack gap="xl">
          <Stack gap="lg">
            <Group justify="space-between" align="center">
              <Title order={3}>Camino</Title>
              <Button
                onClick={handleEditCamino}
                variant="default"
                size="xs"
                leftSection={<Icon name="IconEdit" size={LAYOUT.ARROW_LEFT_ICON_SIZE} />}
              >
                Edit
              </Button>
            </Group>

            <Group gap="md" align="center">
              <Group gap="md" align="center" flex="1">
                <Text className={styles.label} size="sm">
                  Status:
                </Text>
                <Badge color={caminoData?.isActive ? 'green' : 'red'} variant="light">
                  {caminoData?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Group>
            </Group>

            {caminoData?.isActive && (
              <Stack gap="md">
                {caminoData.parishId != null && (
                  <Group gap="md" align="center">
                    <Text className={styles.label}>Parish ID:</Text>
                    <Text className={styles.value}>{caminoData.parishId}</Text>
                  </Group>
                )}
                {caminoData.dioceseId != null && (
                  <Group gap="md" align="center">
                    <Text className={styles.label}>Diocese ID:</Text>
                    <Text className={styles.value}>{caminoData.dioceseId}</Text>
                  </Group>
                )}
                {caminoData.url && (
                  <Group gap="md" align="center">
                    <Text className={styles.label}>URL:</Text>
                    <Text className={styles.value}>{caminoData.url}</Text>
                  </Group>
                )}
                {caminoData.comment && (
                  <Group gap="md" align="flex-end">
                    <Text className={styles.label}>Comment:</Text>
                    <Text className={styles.value}>{caminoData.comment}</Text>
                  </Group>
                )}
              </Stack>
            )}
          </Stack>

          {caminoData?.isActive && (
            <>
              <Divider />
              <CaminoMemberImportForm churchId={churchId} />
              <CaminoMemberImportsTable churchId={churchId} />
            </>
          )}
        </Stack>
      </Paper>

      <EditCaminoModal
        opened={isEditModalOpen}
        caminoData={caminoData}
        onClose={handleCloseEditCamino}
        onSave={handleSaveCamino}
      />
    </>
  );
};

export default Camino;
